import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components/macro';

import Box from '../../elements/Box';
import Button from '../../elements/button';
import H1 from '../../elements/h1';
import Span from 'components/elements/span';

import { ReactComponent as Trashbin } from '../../../icons/icon-trashbin.svg';
import { ReactComponent as Plus } from '../../../icons/icon-plus.svg';
import { ReactComponent as Close } from '../../../icons/icon-close.svg';
import { ReactComponent as Chevron } from '../../../icons/icon-chevron.svg';

import {
  ISubscription,
  removeAllSubscriptions,
} from 'redux/reducers/subscriptions';

import SubscriptionSummary from './SubscriptionSummary';
import CreateSubscription from './CreateSubscription';
import { useDispatch, useSelector } from 'react-redux';
import getMobileOS from 'helpers/getMobileOS';
import useMedia from 'hooks/useMedia';
import { useTopicFilters } from 'components/TopicFilters/utils/useTopicFilters';
import { selectStreamingCustomers } from 'redux/reducers/customers';
import { trackFilter } from 'redux/reducers/usage/filter/filter';

import { RootState } from 'redux/rootReducer';
import { TopicFilterNames } from 'components/TopicFilters/shared';
import { IFilterInfo } from 'redux/reducers/usage/filter/filter';
import { ICountryFlagProps } from 'types/shared';
import { Region } from 'components/notifications/Settings/LocationDropdown';
import { parsePriorityTypes } from './utils/helpers';
import { loadConsulSettings } from 'redux/reducers/consul_parameters';
import checkBetaGroup from 'helpers/checkBetaGroup';

interface IProps {
  artist: undefined | { entityName: string; entityId: string };
  close: () => void;
  subscriptions?: ISubscription[];
  setShowArtistsOverview: () => void;
  loading: boolean;
}

export const Rotate = styled.span`
  display: flex;
  transform: rotate(180deg);

  & > svg {
    margin-left: 0.75em;
  }
`;

const ArtistSettings: React.FC<IProps> = (props) => {
  const { subscriptions, artist, close, loading, setShowArtistsOverview } =
    props;

  const [isShowingNotifierBanner, setIsShowingNotifierBanner] = useState(false);
  const [isAddingNewNotification, setIsAddingNewNotification] =
    useState<boolean>(false);
  const [isCreatingMobileAlert, setIsCreatingMobileAlert] = useState(false);
  const dispatch = useDispatch();
  const isLargeScreen = useMedia('(min-width: 47rem)');
  const isSmallScreen = useMedia('(max-width: 45em)');

  const streamingCustomers = useSelector(selectStreamingCustomers);
  const countries = useSelector((state: RootState) => state.countries.data);
  const regions = useSelector((state: RootState) => state.regions.data);
  const [, recentFilters] = useTopicFilters();
  const saveLocation = (
    filterType: 'country' | 'region',
    locationId: string
  ): any => dispatch(trackFilter(filterType, locationId));

  useEffect(() => {
    dispatch(loadConsulSettings());
  }, [dispatch]);

  const isOpusNotifierOn = useSelector(
    (state: RootState) => state.consulEnvData?.data?.isOpusNotifierOn
  );
  const BetaGroupPriorityPlaylist = useSelector(
    (state: RootState) => state.consulEnvData?.data?.BetaGroupPriorityPlaylist
  );
  const authenticationProfile = useSelector(
    (state: RootState) => state.authentication.profile
  );

  const showOpusNotifier =
    isOpusNotifierOn &&
    checkBetaGroup(BetaGroupPriorityPlaylist, authenticationProfile?.email);

  const getRecentFiltersFor = useCallback(
    (filterName: TopicFilterNames): IFilterInfo[] => {
      const result = recentFilters.filter((recent: any) => {
        return (
          recent.filterType === filterName &&
          typeof recent?.filterValue === 'string' &&
          !String(recent?.filterValue).startsWith('EX')
        );
      });
      return result;
    },
    [recentFilters]
  );

  const sortByDisplayName = (first: any, second: any): any =>
    first.displayName.localeCompare(second.displayName);
  const addLabel = (item: ICountryFlagProps | Region | undefined): any => {
    if (!item) {
      throw new Error(
        'Illegal argument: addLabel received NULL for its item parameter.'
      );
    }
    return { ...item, value: item.id, label: item.displayName };
  };

  const recentCountries = getRecentFiltersFor('country')
    .map((country: any) =>
      countries.find((original: any) => original.id === country.filterValue)
    )
    .sort(sortByDisplayName)
    .map(addLabel)
    .splice(0, 3);
  const recentRegions = getRecentFiltersFor('region')
    .reduce((all: any, region: any) => {
      const selectedRegion = regions.find(
        (original: any) => original.id === region.filterValue
      );
      selectedRegion && all.push(selectedRegion);
      return all;
    }, [])
    .sort(sortByDisplayName)
    .map(addLabel)
    .splice(0, 3);

  const isNewSubscription = !subscriptions || !subscriptions.length;

  function unsubscribe(evt: MouseEvent): void {
    evt.stopPropagation();

    if (!isNewSubscription && artist && !loading) {
      dispatch(removeAllSubscriptions(artist.entityId));
    }

    close();
  }

  const mobileOS = getMobileOS();

  const onClickBack = (): void => setIsShowingNotifierBanner(false);
  const notifierBannerText =
    isSmallScreen && mobileOS !== 'iOS' ? (
      'You subscribed to a mobile alert but Android app is not available yet.'
    ) : (
      <span>
        You subscribed to a mobile alert. {isSmallScreen ? '' : <br />}Do you
        have the Notifier app for iPhone?
      </span>
    );

  return (
    <Box
      m={0}
      p={
        !isLargeScreen && (isNewSubscription || isAddingNewNotification) ? 4 : 6
      }
      minHeight={isSmallScreen ? '100vh' : 'unset'}
    >
      <Box display="flex" justifyContent="space-between">
        <Button
          display="flex"
          alignItems="baseline"
          px={0}
          onClick={
            isShowingNotifierBanner ? onClickBack : setShowArtistsOverview
          }
        >
          <Rotate>
            <Chevron />
          </Rotate>
          <Span lineHeight="0.75em">
            {isShowingNotifierBanner ? 'Back' : 'All artists'}
          </Span>
        </Button>
        <Button onClick={close}>
          <Close />
        </Button>
      </Box>

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
      >
        <H1
          fontSize={4}
          lineHeight="26px"
          my={
            !isLargeScreen &&
            !isShowingNotifierBanner &&
            (isNewSubscription || isAddingNewNotification)
              ? 5
              : 2
          }
        >
          {isShowingNotifierBanner ? (
            notifierBannerText
          ) : (
            <>
              Notification settings{' '}
              {artist && (
                <span>
                  for{' '}
                  <span data-gtag="notifications-modal-artistname">
                    {artist.entityName}
                  </span>
                </span>
              )}
            </>
          )}
        </H1>
        <Box
          display="flex"
          alignItems="center"
          flexDirection={['column', 'row']}
        >
          {subscriptions &&
            subscriptions?.length > 0 &&
            !isShowingNotifierBanner && (
              <Button
                data-gtag={'notifications-modal-unsubscribe'}
                variant={
                  isNewSubscription || loading ? 'disabled' : 'secondary'
                }
                display="flex"
                alignItems="center"
                justifyContent="center"
                marginLeft={[0, 4]}
                px="1.2rem"
                py="0.7rem"
                my={!isLargeScreen ? 2 : 0}
                onClick={unsubscribe as any}
                type="button"
                alignSelf={isLargeScreen ? 'initial' : 'flex-start'}
              >
                <Trashbin />
                Unsubscribe
              </Button>
            )}
        </Box>
      </Box>

      {(isNewSubscription ||
        isAddingNewNotification ||
        isCreatingMobileAlert) &&
        artist && (
          <CreateSubscription
            subscriptions={subscriptions}
            entityId={artist.entityId}
            countries={countries}
            customers={streamingCustomers}
            setIsAddingNewNotification={setIsAddingNewNotification}
            isAddingNewNotification={isAddingNewNotification}
            loading={loading}
            regions={regions}
            recentRegions={recentRegions}
            recentCountries={recentCountries}
            saveLocation={saveLocation}
            setIsShowingNotifierBanner={setIsShowingNotifierBanner}
            isShowingNotifierBanner={isShowingNotifierBanner}
            setIsCreatingMobileAlert={setIsCreatingMobileAlert}
            isOpusNotifierOn={showOpusNotifier}
          />
        )}

      {!isNewSubscription &&
        !isAddingNewNotification &&
        !isCreatingMobileAlert && (
          <Button
            width="100%"
            display="flex"
            alignItems="center"
            variant="secondary"
            marginTop={2}
            py={4}
            fontSize={2}
            fontWeight="600"
            onClick={(): void => setIsAddingNewNotification(true)}
          >
            {subscriptions && subscriptions?.length > 0 && (
              <Box
                display="flex"
                alignItems="center"
                color="AccentGreen"
                marginLeft={isLargeScreen ? 3 : '5px'}
              >
                <Plus />
              </Box>
            )}
            Add new notification
          </Button>
        )}

      {subscriptions &&
        !isShowingNotifierBanner &&
        subscriptions.map((storedSubscription: ISubscription) => (
          <SubscriptionSummary
            key={`${storedSubscription.entityId}-${storedSubscription.countryId}-${storedSubscription.customerId}`}
            countries={countries}
            customers={streamingCustomers}
            subscription={{
              ...storedSubscription,
              eventChannels: parsePriorityTypes(storedSubscription),
            }}
            loading={loading}
            regions={regions}
            isLargeScreen={isLargeScreen}
            isOpusNotifierOn={showOpusNotifier}
          />
        ))}
    </Box>
  );
};

export default ArtistSettings;
